import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useEnableGcpAccount } from "featureFlags/hooks/useEnableGcpAccount";
import { useNewSidebarExperience } from "featureFlags/hooks/useNewSidebarExperience";
import { useDatabasesNames } from "services/databases/useDatabasesNames";
import { PinnedItemType, usePinnedItems } from "services/pinnedItems";
import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";

import { useAccessManager } from "components/App/accessManager";
import Button, { ButtonTemplate } from "components/Button";
import { Tree } from "components/Tree/Tree";
import { ItemDataType, TreeNodeType } from "components/Tree/types";

import { useCurrentAccount } from "../../Account/useCurrentAccount";
import { useCreateDatabase } from "../CreateMenu/useCreateDatabase";
import { useLoadData } from "../CreateMenu/useLoadData";
import { EmptySearch } from "../EmptyState/EmptySearch";
import { DatabaseIcon, EmptyState } from "../EmptyState/EmptyState";
import { Panel } from "../Panel";
import { Search } from "../Search";
import { useLeftSidebarContext } from "../context";
import {
  ColumnLabel,
  DatabaseLabel,
  DefaultLabel,
  IndexLabel,
  InformationSchemaTableLabel,
  TableLabel,
  ViewLabel,
} from "./Labels";
import {
  DatabaseSearchMenu,
  useDatabaseSearch,
  useSearchData,
  useSearchExpandState,
  useSearchFilterSort,
} from "./search";
import { sortWithPinnedFirst } from "./sortWithPinnedFirst";
import { useChildren } from "./useChildren";
import { useDeleteDatabase } from "./useDeleteDatabase";
import { useEditDatabase } from "./useEditDatabase";

import styles from "../styles.module.scss";

const EmptyLabel = () => {
  return <div className={styles.empty}>No objects found.</div>;
};

const SeparatorLabel = () => {
  return <div className={styles.separator} />;
};

const labelMap = {
  database: DatabaseLabel,
  table: TableLabel,
  view: ViewLabel,
  external_table: TableLabel,
  information_schema_table: InformationSchemaTableLabel,
  index: IndexLabel,
  column: ColumnLabel,
  information_schema_column: ColumnLabel,
  external_table_column: ColumnLabel,
  view_column: ColumnLabel,
  separator: SeparatorLabel,
  empty: EmptyLabel,
};

const collectNodesWithChildren = (
  nodes: ItemDataType[],
  result: Set<string>
) => {
  nodes.forEach(node => {
    if (Array.isArray(node.children) && node.children.length > 0) {
      result.add(node.value as string);
      collectNodesWithChildren(node.children, result);
    }
  });
};

export const DatabasesPanel = React.memo(() => {
  const { t } = useTranslation();
  const { createDatabaseMarkup, openCreateDatabase } = useCreateDatabase();
  const databases = useDatabasesNames({ showSystemDatabases: true });
  const newSidebarExperience = useNewSidebarExperience();
  const { pinnedItems, togglePinned } = usePinnedItems(PinnedItemType.DATABASE);
  const {
    onDatabaseSearchChange,
    databaseSearchState,
    onExpand,
    expandedItems,
    treeData,
  } = useLeftSidebarContext();
  const { isAllowedTo } = useAccessManager();
  const enabledGcpAccount = useEnableGcpAccount();
  const { deleteDatabase, deleteDatabaseMarkup } = useDeleteDatabase();
  const { openEditDatabase, editDatabaseMarkup } = useEditDatabase();
  const { openLoadData, loadDataMarkup } = useLoadData();
  const { getAccount } = useCurrentAccount();
  const { getChildren } = useChildren();

  const {
    data: fullSearchData,
    newSearchExperience,
    isLoading,
    searchTerm: resolvedSearchTerm,
    isLegacySearch,
  } = useSearchData(databases, databaseSearchState);

  const { inputValue: searchInputValue, handleChange: handleSearchChange } =
    useDatabaseSearch({
      initialValue: databaseSearchState,
      isLegacySearch,
      onSearchChange: onDatabaseSearchChange,
    });

  const accountName = getAccount().accountName;
  const pinnedSet = useMemo(() => new Set(pinnedItems), [pinnedItems]);

  const baseTreeData = useMemo(() => {
    const databasesMap = new Map<string, ItemDataType>(
      treeData.map(item => [item.databaseName as string, item])
    );
    return databases.map(database => {
      const { catalogName } = database;
      return {
        type: "database",
        label: catalogName,
        value: catalogName,
        payload: database,
        databaseName: catalogName,
        children: databasesMap.get(catalogName)?.children || [],
      };
    });
  }, [databases, treeData]);

  const data = useMemo(() => {
    if (!newSidebarExperience) {
      return baseTreeData;
    }
    return sortWithPinnedFirst(baseTreeData, pinnedSet);
  }, [baseTreeData, newSidebarExperience, pinnedSet]);

  const {
    filteredTreeData,
    expandedOnSearch,
    searchCounts,
    filterType,
    sortType,
    setFilterType,
    setSortType,
    resetSearchOptions,
  } = useSearchFilterSort({
    fullSearchData,
    newSearchExperience,
    searchTerm: resolvedSearchTerm,
  });

  const isSearchActive =
    newSearchExperience && resolvedSearchTerm.trim().length > 0;

  const treeDataToRender = useMemo(() => {
    if (!isSearchActive) {
      return data;
    }
    if (!newSidebarExperience) {
      return filteredTreeData;
    }
    return sortWithPinnedFirst(filteredTreeData, pinnedSet);
  }, [data, filteredTreeData, isSearchActive, newSidebarExperience, pinnedSet]);

  const { expandedItems: computedExpandedItems, handleExpand } =
    useSearchExpandState(
      expandedItems,
      expandedOnSearch,
      isSearchActive,
      onExpand
    );

  const validExpandedItems = useMemo(() => {
    const nodesWithChildren = new Set<string>();
    collectNodesWithChildren(treeDataToRender, nodesWithChildren);
    return computedExpandedItems.filter(item => nodesWithChildren.has(item));
  }, [computedExpandedItems, treeDataToRender]);

  const prevIsSearchActive = useRef(isSearchActive);
  useEffect(() => {
    if (!isSearchActive && prevIsSearchActive.current) {
      resetSearchOptions();
    }
    prevIsSearchActive.current = isSearchActive;
  }, [isSearchActive, resetSearchOptions]);

  const actions = useMemo(
    () => ({
      deleteDatabase,
      openLoadData,
      openEditDatabase,
      showPin: newSidebarExperience,
      isPinned: (databaseName: string) => pinnedSet.has(databaseName),
      onTogglePin: newSidebarExperience ? togglePinned : undefined,
    }),
    [
      deleteDatabase,
      newSidebarExperience,
      openEditDatabase,
      openLoadData,
      pinnedSet,
      togglePinned,
    ]
  );

  const renderLabel = useCallback(
    (node: TreeNodeType) => {
      const Label =
        labelMap[node.type as keyof typeof labelMap] || DefaultLabel;
      return (
        <Label
          node={node}
          actions={actions}
        />
      );
    },
    [actions]
  );

  const allowedToCreateDatabase =
    isAllowedTo(
      PrivilegeObjectType.account,
      accountName,
      SqlRbacAction.DATABASE_CREATE
    ) && !enabledGcpAccount;

  const showSearchMenu =
    newSearchExperience && !!databaseSearchState.trim().length;

  const menuControl = showSearchMenu ? (
    <DatabaseSearchMenu
      filterType={filterType}
      sortType={sortType}
      counts={searchCounts}
      onFilterChange={setFilterType}
      onSortChange={setSortType}
    />
  ) : null;

  const emptyStateContent = (
    <EmptyState
      icon={<DatabaseIcon />}
      title={t("workspace.databases.empty")}
      subtitle={
        allowedToCreateDatabase
          ? t("workspace.databases.empty_subtitle")
          : undefined
      }
      button={
        allowedToCreateDatabase
          ? t("workspace.databases.empty_button")
          : undefined
      }
      buttonTemplate={ButtonTemplate.Ghost}
      content={
        allowedToCreateDatabase ? (
          <Button
            dataTestid="db-empty-list-load-data-button"
            text={t("workspace.create_menu.load_data")}
            template={ButtonTemplate.Primary}
            onClick={() => openLoadData()}
          />
        ) : undefined
      }
      onClick={openCreateDatabase}
      testId="databases-empty-list"
    />
  );

  if (!databases.length) {
    return (
      <>
        <Search
          onChange={handleSearchChange}
          value={searchInputValue}
          testId="databases-search-input"
          disabled
          isLoading={isLoading}
        />
        {emptyStateContent}
        {createDatabaseMarkup}
        {loadDataMarkup}
      </>
    );
  }

  return (
    <>
      <Search
        onChange={handleSearchChange}
        value={searchInputValue}
        testId="databases-search-input"
        controls={menuControl}
        isLoading={isLoading}
      />
      <Panel testId="databases-list-panel">
        <div
          className={cn(styles.treeWrapper, {
            [styles.treeDisabled]: isLoading,
          })}
        >
          <Tree
            controlledExpandedItems={validExpandedItems}
            onExpand={handleExpand}
            data={treeDataToRender}
            getChildren={getChildren}
            renderLabel={renderLabel}
            searchKeyword={databaseSearchState}
            useInternalSearch={!newSearchExperience}
            shouldPreserveTreeData={!isSearchActive}
            nodeClassName={styles.node}
            treeClassName={styles.tree}
            emptySearchState={
              <EmptySearch
                search={databaseSearchState}
                testId="empty-databases-search"
              />
            }
            emptyState={
              <EmptyState
                icon={<DatabaseIcon />}
                title={t("workspace.databases.empty")}
                subtitle={
                  allowedToCreateDatabase
                    ? t("workspace.databases.empty_subtitle")
                    : undefined
                }
                button={
                  allowedToCreateDatabase
                    ? t("workspace.databases.empty_button")
                    : undefined
                }
                onClick={openCreateDatabase}
                testId="databases-empty-list"
              />
            }
            disabled={isLoading}
          />
        </div>
        {deleteDatabaseMarkup}
        {editDatabaseMarkup}
        {loadDataMarkup}
      </Panel>
    </>
  );
});
